/* eslint-disable jsx-a11y/label-has-associated-control */
import { Content } from "@iventis/translations";
import { TextField } from "@mui/material";
import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { styled, media, muiInputFormsCSS, InteractiveElement, StyledFieldLabel, Body1, FormLabel, Body2, formFieldVerticalGap, SolidButton, TextButton } from "@iventis/styles";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { dataTestIds } from "@iventis/testing";

interface LoginFormProps {
    initEmail: string;
    login: (email: string, password: string) => void;
    loginError: string;
    forgottenPasswordClicked: () => void;
    showSsoLink: boolean;
    ssoLinkClicked?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ initEmail, login, loginError, forgottenPasswordClicked, showSsoLink, ssoLinkClicked }) => {
    const translate = useIventisTranslate();

    return (
        <StyledFormContainer>
            <Formik
                initialValues={{
                    password: "",
                    email: initEmail,
                    details: false,
                }}
                validationSchema={Yup.object({
                    password: Yup.string().required(translate(Content.login.error.required)),
                    email: Yup.string().email(translate(Content.login.error.invalid)).required(translate(Content.login.error.required)),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await login(values.email, values.password);
                }}
            >
                {(props) => {
                    const { errors, values, touched, handleChange, handleSubmit, isSubmitting } = props;
                    return (
                        <StyledLoginForm
                            onSubmit={handleSubmit}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: formFieldVerticalGap,
                            }}
                            className="EmailForm"
                        >
                            <div style={{ height: "80px" }}>
                                <StyledFieldLabel>
                                    <FormLabel id="password-label">{translate(Content.login.labels.password)}</FormLabel>
                                </StyledFieldLabel>
                                <TextField
                                    inputProps={{ "data-testid": dataTestIds.login.passwordInput }}
                                    variant="outlined"
                                    aria-labelledby="password-label"
                                    type="password"
                                    name="password"
                                    data-cy="password"
                                    style={{ minWidth: "100%" }}
                                    placeholder={translate(Content.login.placeholders.password)}
                                    error={errors.password && touched.password}
                                    value={values.password}
                                    onChange={handleChange}
                                    helperText={errors.password && touched.password && errors.password}
                                    autoComplete="current-password"
                                />
                                <Body1>{loginError}</Body1>
                            </div>
                            <StyledLinksContainer>
                                <StyledLink type="button" onClick={forgottenPasswordClicked}>
                                    <Body2>{translate(Content.login.links.forgotPass)}</Body2>
                                </StyledLink>
                                {showSsoLink && (
                                    <StyledLink type="button" onClick={ssoLinkClicked}>
                                        <Body2>{translate(Content.login.links.loginWithSingleSignOn)}</Body2>
                                    </StyledLink>
                                )}
                            </StyledLinksContainer>
                            <StyledSubmitContainer>
                                <StyledSubmitButton disabled={isSubmitting} data-testid={dataTestIds.login.submitButton} type="submit" data-cy="login">
                                    {translate(Content.login.buttons.login)}
                                </StyledSubmitButton>
                            </StyledSubmitContainer>
                        </StyledLoginForm>
                    );
                }}
            </Formik>
        </StyledFormContainer>
    );
};

export const StyledLoginForm = styled.form`
    ${muiInputFormsCSS};
    min-width: 100%;
    ${media.extraSmall} {
        padding: 0 0 0 0;
    }
`;

export const StyledTextButton = styled(TextButton)`
    width: 50%;
`;

export const StyledLinksContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export const StyledSubmitContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const StyledLink = styled(InteractiveElement)`
    text-align: start;
    text-decoration: underline;
    cursor: pointer;
`;

const StyledFormContainer = styled.div`
    display: flex;
    align-self: flex-end;
    min-width: 100%;
`;

export const StyledSubmitButton = styled(SolidButton)`
    width: 158px;
`;

export default LoginForm;
